import {
  Box,
  Button,
  ErrorMessage,
  Flex,
  Grid,
  Modal,
  Tooltip
} from '@sequencehq/core-components'
import { match } from 'ts-pattern'
import { IntegrationCustomerPicker } from 'Integrations/index'
import { IntegrationMoreInfo } from 'lib/docsLink/integrations'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { MODAL_CONTENT_HEIGHT_PX } from '../constants'
import { useCustomerSelection } from '../hooks/useCustomerSelection'
import {
  useLegacyImportSubmit,
  useSyncImportSubmit
} from '../hooks/useImportSubmit'
import { SelectionFooter } from '../components/SelectionFooter'
import { HubspotImportStagesInfo } from '../utils/HubspotImportStagesInfo'

const SELECTION_TEST_ID = 'importCustomers.customerSelection'

export type CustomerSelectionImportStarted = {
  customerIds: string[]
  customerNames: Record<string, string>
  startedAt?: string
}

export const CustomerSelection = (props: {
  isFirstStep?: boolean
  onBack: () => void
  onImportStarted: (started: CustomerSelectionImportStarted) => void
}) => {
  const { hubspotV2IntegrationEnabled } = useFeatureFlags()
  const {
    isLoading,
    isError,
    selectedIntegration,
    filteredCustomers,
    countryOptions,
    countryFilter,
    onCountryFilterChange,
    hasActiveFilters,
    onClearFilters,
    unfilteredCustomerCount,
    allCustomersLinked,
    selectableIdsInView,
    allInViewSelected,
    onToggleSelectAllInView,
    onToggleCustomer,
    integrationName,
    search,
    setSearch,
    selectedCustomerIds,
    selectedIdsInView,
    setSelectedCustomerIds,
    submitDisabled,
    submitDisabledReason
  } = useCustomerSelection()

  const isHubspotSync =
    selectedIntegration === 'HubSpot' && Boolean(hubspotV2IntegrationEnabled)

  const legacySubmit = useLegacyImportSubmit({
    selectedIntegration,
    selectedIdsInView,
    integrationName
  })
  const syncSubmit = useSyncImportSubmit({
    selectedIntegration,
    selectedIdsInView,
    integrationName
  })
  const { onSubmit, isSubmitting } = isHubspotSync ? syncSubmit : legacySubmit

  return (
    <>
      <Modal.Content minHeight={`${MODAL_CONTENT_HEIGHT_PX}px`}>
        {match({
          isLoading,
          isError
        })
          .with({ isLoading: true }, () => (
            <Modal.LoadingSpinner
              height={`${MODAL_CONTENT_HEIGHT_PX - 40}px`}
            />
          ))
          .with({ isError: true }, () => (
            <Flex
              height={`${MODAL_CONTENT_HEIGHT_PX - 40}px`}
              width="100%"
              alignItems="center"
              justifyContent="center"
            >
              <ErrorMessage message="Could not load connector customers." />
            </Flex>
          ))
          .otherwise(() => (
            <Grid
              gridTemplateRows="minmax(0, 1fr) auto"
              gridTemplateColumns="minmax(0, 1fr)"
              height={`${MODAL_CONTENT_HEIGHT_PX + 120}px`}
              width="100%"
            >
              <IntegrationCustomerPicker
                action="import"
                heading="Choose customers to import"
                subHeaderInfo={
                  isHubspotSync ? <HubspotImportStagesInfo /> : undefined
                }
                customers={filteredCustomers}
                search={search}
                onSearchChange={setSearch}
                countryFilter={{
                  options: countryOptions,
                  values: countryFilter,
                  onChange: onCountryFilterChange
                }}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={onClearFilters}
                selectedCustomerIds={selectedCustomerIds}
                onToggleCustomer={onToggleCustomer}
                selectAll={{
                  selectableCount: selectableIdsInView.length,
                  allSelected: allInViewSelected,
                  onToggle: onToggleSelectAllInView
                }}
                integrationName={integrationName}
                unfilteredCustomerCount={unfilteredCustomerCount}
                allCustomersLinked={allCustomersLinked}
                data-testid={SELECTION_TEST_ID}
              />
              <SelectionFooter
                selectedCount={selectedIdsInView.length}
                learnMoreHref={
                  isHubspotSync ? IntegrationMoreInfo.HubSpotLink : undefined
                }
              />
            </Grid>
          ))}
      </Modal.Content>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            setSelectedCustomerIds([])
            props.onBack()
          }}
        >
          {props.isFirstStep ? 'Cancel' : 'Back'}
        </Button>
        <Tooltip
          label={
            <Box data-testid={`${SELECTION_TEST_ID}.continue.tooltip`}>
              {submitDisabledReason}
            </Box>
          }
          isDisabled={!submitDisabled}
        >
          <Button
            disabled={submitDisabled}
            onClick={async () => {
              const submittedIds = selectedIdsInView
              const customerNames = filteredCustomers.reduce<
                Record<string, string>
              >(
                (names, customer) =>
                  submittedIds.includes(customer.id)
                    ? { ...names, [customer.id]: customer.name }
                    : names,
                {}
              )
              const result = await onSubmit()
              if (result.started) {
                props.onImportStarted({
                  customerIds: submittedIds,
                  customerNames,
                  startedAt: result.startedAt
                })
              }
            }}
            submitting={isSubmitting}
            submittingText="Starting import..."
            data-testid={`${SELECTION_TEST_ID}.continue`}
          >
            Start import
          </Button>
        </Tooltip>
      </Modal.Footer>
    </>
  )
}
